import { useEffect, useMemo, useState } from 'react';
import { Button, Input, Radio, Select } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrashCan } from '@fortawesome/pro-light-svg-icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { isValidBusinessId, normLocation } from '@salaries/shared';
import { getPnlUnits, savePnlUnits } from '../../api/pnlUnits';
import { SettingCard } from '../../components/SettingCard';

// A unit being edited. `id` (>0) is preserved on save so stored lines keep their
// unit; new units have id undefined.
interface DraftUnit {
  id?: number;
  name: string;
  is_primary: boolean;
  locations: string[];
  business_id?: string;
}

/**
 * Self-contained P&L-units settings card (its own endpoint + save button, like
 * the hugim overrides section). Only meaningful when the box has more than one
 * location; otherwise it explains that and stays disabled. On enable, every box
 * location must be assigned to exactly one unit — the server blocks the save and
 * we surface its message.
 */
export function PnlUnitsCard() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['pnl-units'], queryFn: getPnlUnits });

  const [enabled, setEnabled] = useState(false);
  const [units, setUnits] = useState<DraftUnit[]>([]);

  useEffect(() => {
    if (!data) return;
    setEnabled(data.config.enabled);
    setUnits(
      data.config.units.map((u) => ({
        id: u.id,
        name: u.name,
        is_primary: u.is_primary,
        locations: u.locations,
        business_id: u.business_id ?? '',
      })),
    );
  }, [data]);

  const locations = data?.locations ?? [];
  const multiLocation = locations.length > 1;

  // Locations already taken by OTHER units → excluded from each unit's options
  // (a location belongs to exactly one unit).
  const assignedElsewhere = (idx: number) => {
    const taken = new Set<string>();
    units.forEach((u, i) => {
      if (i !== idx) u.locations.forEach((l) => taken.add(normLocation(l)));
    });
    return taken;
  };

  const allAssigned = useMemo(() => {
    const taken = new Set(units.flatMap((u) => u.locations).map(normLocation));
    return locations.filter((l) => !taken.has(normLocation(l)));
  }, [units, locations]);

  const mutation = useMutation({
    mutationFn: () =>
      savePnlUnits({
        enabled,
        units: units.map((u) => ({
          id: u.id,
          name: u.name.trim(),
          is_primary: u.is_primary,
          locations: u.locations,
          business_id: u.business_id?.trim() || null,
        })),
      }),
    onSuccess: () => {
      toast.success('יחידות הרווח וההפסד נשמרו');
      void queryClient.invalidateQueries({ queryKey: ['pnl-units'] });
      void queryClient.invalidateQueries({ queryKey: ['payroll-report'] });
    },
    onError: (err) => toast.error((err as Error).message || 'שגיאה בשמירת היחידות'),
  });

  const addUnit = () =>
    setUnits((prev) => [
      ...prev,
      { name: '', is_primary: prev.length === 0, locations: [] },
    ]);

  const removeUnit = (idx: number) =>
    setUnits((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      // Keep exactly one primary.
      if (next.length > 0 && !next.some((u) => u.is_primary)) next[0]!.is_primary = true;
      return next;
    });

  const setUnit = (idx: number, patch: Partial<DraftUnit>) =>
    setUnits((prev) => prev.map((u, i) => (i === idx ? { ...u, ...patch } : u)));

  const setPrimary = (idx: number) =>
    setUnits((prev) => prev.map((u, i) => ({ ...u, is_primary: i === idx })));

  const summary = !multiLocation ? 'דורש יותר מסניף אחד' : enabled ? `${units.length} יחידות` : 'כבוי';

  return (
    <SettingCard id="pnl" title="יחידות רווח והפסד" summary={summary}>
      {!multiLocation ? (
        <div className="text-small text-secondary-text">
          יחידות רווח והפסד זמינות רק לבוקס עם יותר מסניף אחד. כאשר יוגדר סניף נוסף, ניתן יהיה
          לפצל את השכר לפי יחידות.
        </div>
      ) : (
        <>
          <div className="text-small text-secondary-text mb-3">
            פיצול השכר לפי יחידות רווח והפסד: כל יחידה מאגדת סניפים. השכר של כל איש צוות יפוצל לפי
            הסניף של השיעור, תופיע שורה נפרדת בטבלה לכל יחידה, וכל יחידה תקבל מייל אישור נפרד. שכר
            בסיס, נסיעות ובונוסים שאינם תלויי-סניף ישויכו ליחידה הראשית של איש הצוות.
          </div>

          <Radio.Group
            value={enabled ? 'on' : 'off'}
            onChange={(e) => setEnabled(e.target.value === 'on')}
            className="mb-3"
          >
            <Radio value="off">כבוי</Radio>
            <Radio value="on">פעיל</Radio>
          </Radio.Group>

          {enabled && (
            <div className="flex flex-col gap-3">
              {units.map((u, idx) => {
                const taken = assignedElsewhere(idx);
                const options = locations
                  .filter((l) => !taken.has(normLocation(l)))
                  .map((l) => ({ label: l, value: l }));
                return (
                  <div key={idx} className="border border-border rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Input
                        value={u.name}
                        placeholder="שם היחידה"
                        onChange={(e) => setUnit(idx, { name: e.target.value })}
                      />
                      <button
                        type="button"
                        aria-label="מחק יחידה"
                        onClick={() => removeUnit(idx)}
                        className="p-2 rounded text-secondary-text hover:bg-grey-bg hover:text-error-red"
                      >
                        <FontAwesomeIcon icon={faTrashCan} />
                      </button>
                    </div>
                    <Select
                      mode="multiple"
                      value={u.locations}
                      onChange={(vals) => setUnit(idx, { locations: vals })}
                      options={options}
                      placeholder="סניפים ביחידה"
                      className="w-full mb-2"
                    />
                    <Input
                      value={u.business_id ?? ''}
                      placeholder="ח״פ / מספר עוסק של היחידה (לבדיקת החשבוניות)"
                      inputMode="numeric"
                      dir="ltr"
                      status={u.business_id && !isValidBusinessId(u.business_id) ? 'error' : undefined}
                      onChange={(e) => setUnit(idx, { business_id: e.target.value.replace(/\D/g, '') })}
                      className="mb-2"
                    />
                    <Radio checked={u.is_primary} onChange={() => setPrimary(idx)}>
                      יחידה ראשית (מקבלת שכר בסיס, נסיעות ובונוסים)
                    </Radio>
                  </div>
                );
              })}

              <div>
                <Button icon={<FontAwesomeIcon icon={faPlus} />} onClick={addUnit}>
                  הוסף יחידה
                </Button>
              </div>

              {allAssigned.length > 0 && (
                <div className="text-small text-error-red">
                  יש לשייך כל סניף ליחידה. סניפים שלא שויכו: {allAssigned.join(', ')}
                </div>
              )}
            </div>
          )}

          <div className="mt-3">
            <Button
              type="primary"
              loading={mutation.isPending || isLoading}
              onClick={() => mutation.mutate()}
            >
              שמירת יחידות
            </Button>
          </div>
        </>
      )}
    </SettingCard>
  );
}
